// src/components/PartnersGrid.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaHandshake,
} from "react-icons/fa";

// 🔗 Partenaires
const PARTNERS = [
  {
    name: "Bordeaux Métropole",
    url: "https://www.bordeaux-metropole.fr/",
    logo: "/img/logos/logo_BM.webp",
  },
  {
    name: "Ligue Nouvelle-Aquitaine Judo",
    url: "https://nouvelle-aquitaine-judo.ffjudo.com/",
    logo: "/img/logos/logo_NA_fondblanc.jpg",
  },
  {
    name: "Ville de Lormont",
    url: "https://www.lormont.fr/",
    logo: "/img/logos/logo_lormont.jpg",
  },
  {
    name: "Conseil Départemental de la Gironde",
    url: "https://www.gironde.fr/",
    logo: "/img/logos/logo_departement.png",
  },
  {
    name: "Ippon Star",
    url: "https://www.ipponstar.com/boutique/fr/",
    logo: "https://www.ipponstar.com/boutique/img/logo-1692950540.jpg",
  },
  {
    name: "Ministère des Sports",
    url: "https://www.sports.gouv.fr/",
    logo: "/img/logos/logo_ministere.png",
  },
  {
    name: "Agence Nationale du Sport",
    url: "https://www.agencedusport.fr/",
    logo: "/img/logos/logo_ans.png",
  },
  {
    name: "Région Nouvelle-Aquitaine",
    url: "https://www.nouvelle-aquitaine.fr/",
    logo: "/img/logos/logo_region_na.png",
  },
  {
    name: "Service Civique",
    url: "https://www.service-civique.gouv.fr/",
    logo: "https://www.service-civique.gouv.fr/assets/img/logo_asc_header.webp",
  },
];

const cx = (...classes) => classes.filter(Boolean).join(" ");

function PartnerLogo({ partner, eager = false }) {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="flex h-full w-full items-center justify-center px-4 text-center">
        <span className="text-sm font-black leading-tight text-slate-500 dark:text-slate-300">
          {partner.name}
        </span>
      </div>
    );
  }

  return (
    <img
      src={partner.logo}
      alt={`Logo ${partner.name}`}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={() => setHasError(true)}
      className="
        max-h-full
        max-w-full
        object-contain
        transition-transform
        duration-500
        ease-out
        group-hover/card:scale-105
      "
    />
  );
}

function PartnerCard({
  partner,
  variant = "desktop",
  duplicate = false,
  eager = false,
}) {
  const isMobile = variant === "mobile";

  return (
    <a
      href={partner.url}
      target="_blank"
      rel="noopener noreferrer"
      tabIndex={duplicate ? -1 : undefined}
      aria-hidden={duplicate ? true : undefined}
      aria-label={`Découvrir ${partner.name}`}
      className={cx(
        "group/card relative isolate overflow-hidden",
        "border border-slate-200/80 bg-white",
        "shadow-[0_18px_45px_-28px_rgba(15,23,42,0.45)]",
        "transition-all duration-500 ease-out",
        "dark:border-white/10 dark:bg-slate-900",
        "focus-visible:outline-none",
        "focus-visible:ring-4 focus-visible:ring-blue-500/30",

        isMobile
          ? [
              "flex min-h-[220px] w-[82vw] max-w-[350px]",
              "shrink-0 snap-center flex-col",
              "rounded-[28px] p-5",
              "active:scale-[0.98]",
            ].join(" ")
          : [
              "flex h-40 w-60 shrink-0 flex-col",
              "rounded-3xl p-4",
              "hover:-translate-y-2",
              "hover:border-blue-400/50",
              "hover:shadow-[0_28px_70px_-30px_rgba(37,99,235,0.55)]",
              "dark:hover:border-blue-400/40",
            ].join(" "),
      )}
    >
      {/* Halo animé au survol */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -right-12 -top-12
          h-28 w-28
          rounded-full
          bg-blue-500/10
          blur-2xl
          transition-all
          duration-700
          group-hover/card:scale-150
          group-hover/card:bg-blue-500/20
          dark:bg-blue-400/10
        "
      />

      <div
        className={cx(
          "relative z-10 flex items-center justify-center",
          "rounded-2xl border border-slate-100",
          "bg-white",
          "shadow-inner shadow-slate-100/70",
          "dark:border-slate-700/80",
          isMobile ? "h-32 px-6 py-5" : "h-24 px-5 py-4",
        )}
      >
        <PartnerLogo partner={partner} eager={eager} />
      </div>

      <div
        className={cx(
          "relative z-10 flex flex-1 items-center justify-between gap-3",
          isMobile ? "mt-5" : "mt-3",
        )}
      >
        <span
          className={cx(
            "font-black leading-tight text-slate-800 dark:text-white",
            isMobile ? "text-base" : "line-clamp-2 text-sm",
          )}
        >
          {partner.name}
        </span>

        <span
          className={cx(
            "flex shrink-0 items-center justify-center rounded-full",
            "bg-blue-50 text-blue-600",
            "transition-all duration-300",
            "group-hover/card:bg-blue-600",
            "group-hover/card:text-white",
            "dark:bg-blue-500/15 dark:text-blue-300",
            isMobile ? "h-10 w-10" : "h-8 w-8",
          )}
        >
          <FaExternalLinkAlt
            aria-hidden="true"
            className={isMobile ? "text-sm" : "text-xs"}
          />
        </span>
      </div>
    </a>
  );
}

export default function PartnersGrid({
  partners = PARTNERS,
  title = "Nos partenaires",
  subtitle = "Ils nous accompagnent dans le développement du judo en Nouvelle-Aquitaine.",
}) {
  const sectionRef = useRef(null);
  const mobileScrollerRef = useRef(null);
  const mobileCardRefs = useRef([]);
  const scrollAnimationRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const safePartners = useMemo(() => {
    if (!Array.isArray(partners)) return [];

    return partners.filter(
      (partner) => partner && partner.name && partner.url && partner.logo,
    );
  }, [partners]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const updateViewport = () => {
      setIsMobileViewport(mediaQuery.matches);
    };

    updateViewport();

    mediaQuery.addEventListener?.("change", updateViewport);

    return () => {
      mediaQuery.removeEventListener?.("change", updateViewport);
    };
  }, []);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSectionVisible(entry.isIntersecting);
      },
      {
        threshold: 0.25,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();

    mediaQuery.addEventListener?.("change", updatePreference);

    return () => {
      mediaQuery.removeEventListener?.("change", updatePreference);
    };
  }, []);

  const scrollToPartner = (index) => {
    if (!safePartners.length) return;

    const scroller = mobileScrollerRef.current;

    if (!scroller) return;

    const normalizedIndex = (index + safePartners.length) % safePartners.length;

    const targetCard = mobileCardRefs.current[normalizedIndex];

    if (!targetCard) return;

    /*
     * Position horizontale permettant de centrer la carte
     * sans modifier le scroll vertical de la page.
     */
    const targetLeft =
      targetCard.offsetLeft -
      (scroller.clientWidth - targetCard.offsetWidth) / 2;

    scroller.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });

    setActiveIndex(normalizedIndex);
  };

  const handlePrevious = () => {
    scrollToPartner(activeIndex - 1);
  };

  const handleNext = () => {
    scrollToPartner(activeIndex + 1);
  };

  const handleMobileScroll = () => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }

    scrollAnimationRef.current = requestAnimationFrame(() => {
      const scroller = mobileScrollerRef.current;

      if (!scroller) return;

      const scrollerCenter =
        scroller.getBoundingClientRect().left + scroller.clientWidth / 2;

      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      mobileCardRefs.current.forEach((card, index) => {
        if (!card) return;

        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - scrollerCenter);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      setActiveIndex(nearestIndex);
    });
  };

  useEffect(() => {
    if (
      !isMobileViewport ||
      !isSectionVisible ||
      safePartners.length <= 1 ||
      prefersReducedMotion ||
      isInteracting
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => {
        const nextIndex = (currentIndex + 1) % safePartners.length;

        const scroller = mobileScrollerRef.current;
        const targetCard = mobileCardRefs.current[nextIndex];

        if (scroller && targetCard) {
          const targetLeft =
            targetCard.offsetLeft -
            (scroller.clientWidth - targetCard.offsetWidth) / 2;

          scroller.scrollTo({
            left: Math.max(0, targetLeft),
            behavior: "smooth",
          });
        }

        return nextIndex;
      });
    }, 4500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    isInteracting,
    isMobileViewport,
    isSectionVisible,
    prefersReducedMotion,
    safePartners.length,
  ]);

  useEffect(() => {
    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, []);

  if (!safePartners.length) return null;

  return (
    <section
      ref={sectionRef}
      className="
    relative overflow-hidden
    bg-gradient-to-b
    from-slate-50 via-white to-slate-100
    px-0 py-16
    font-satoshi
    dark:from-slate-950
    dark:via-slate-900
    dark:to-slate-950
    md:py-20
  "
    >
      <style>
        {`
          @keyframes partners-marquee {
            from {
              transform: translate3d(0, 0, 0);
            }

            to {
              transform: translate3d(-50%, 0, 0);
            }
          }

          .partners-marquee-track {
            animation: partners-marquee 34s linear infinite;
            will-change: transform;
          }

          .partners-marquee-wrapper:hover .partners-marquee-track,
          .partners-marquee-wrapper:focus-within .partners-marquee-track {
            animation-play-state: paused;
          }

          .partners-mobile-scrollbar {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .partners-mobile-scrollbar::-webkit-scrollbar {
            display: none;
          }

          @media (prefers-reduced-motion: reduce) {
            .partners-marquee-track {
              animation: none;
              transform: translate3d(0, 0, 0);
            }
          }
        `}
      </style>

      {/* Décor de fond */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute left-[-120px] top-16
          h-72 w-72
          rounded-full
          bg-blue-500/10
          blur-3xl
          dark:bg-blue-500/10
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute bottom-[-150px] right-[-100px]
          h-80 w-80
          rounded-full
          bg-orange-400/10
          blur-3xl
          dark:bg-orange-500/10
        "
      />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* En-tête */}
        <div className="px-5 text-center sm:px-8">
          <div
            className="
              mx-auto mb-4
              inline-flex items-center gap-2
              rounded-full
              border border-blue-200/80
              bg-blue-50/80
              px-4 py-2
              text-xs font-black uppercase
              tracking-[0.16em]
              text-blue-700
              shadow-sm
              backdrop-blur
              dark:border-blue-400/20
              dark:bg-blue-500/10
              dark:text-blue-300
            "
          >
            <FaHandshake aria-hidden="true" />
            Ils nous soutiennent
          </div>

          <h2
            className="
              text-2xl font-black tracking-tight
              text-slate-950
              dark:text-white
              sm:text-3xl
              lg:text-4xl
            "
          >
            {title}
          </h2>

          {subtitle ? (
            <p
              className="
                mx-auto mt-3 max-w-2xl
                text-sm font-medium leading-relaxed
                text-slate-600
                dark:text-slate-300
                sm:text-base
              "
            >
              {subtitle}
            </p>
          ) : null}
        </div>

        {/* ===================================================== */}
        {/* MOBILE : carrousel tactile avec grandes cartes         */}
        {/* ===================================================== */}

        <div className="mt-9 md:hidden">
          <div
            ref={mobileScrollerRef}
            onScroll={handleMobileScroll}
            onPointerDown={() => setIsInteracting(true)}
            onPointerUp={() => setIsInteracting(false)}
            onPointerCancel={() => setIsInteracting(false)}
            onMouseEnter={() => setIsInteracting(true)}
            onMouseLeave={() => setIsInteracting(false)}
            className="
              partners-mobile-scrollbar
              flex snap-x snap-mandatory
              gap-4 overflow-x-auto
              scroll-smooth
              px-[9vw] pb-6
              overscroll-x-contain
            "
            aria-label="Liste des partenaires"
          >
            {safePartners.map((partner, index) => (
              <div
                key={partner.name}
                ref={(element) => {
                  mobileCardRefs.current[index] = element;
                }}
                className="shrink-0 snap-center"
              >
                <PartnerCard
                  partner={partner}
                  variant="mobile"
                  eager={index === 0}
                />
              </div>
            ))}
          </div>

          {/* Navigation mobile */}
          <div className="mt-1 flex items-center justify-center gap-4 px-5">
            <button
              type="button"
              onClick={handlePrevious}
              aria-label="Afficher le partenaire précédent"
              className="
                flex h-11 w-11 items-center justify-center
                rounded-full
                border border-slate-200
                bg-white
                text-slate-700
                shadow-lg shadow-slate-900/10
                transition
                duration-300
                active:scale-90
                dark:border-slate-700
                dark:bg-slate-900
                dark:text-white
              "
            >
              <FaChevronLeft aria-hidden="true" />
            </button>

            <div
              className="
                flex max-w-[190px]
                items-center justify-center
                gap-1.5
                overflow-hidden
              "
              aria-label={`Partenaire ${activeIndex + 1} sur ${
                safePartners.length
              }`}
            >
              {safePartners.map((partner, index) => {
                const isActive = activeIndex === index;

                return (
                  <button
                    key={partner.name}
                    type="button"
                    onClick={() => scrollToPartner(index)}
                    aria-label={`Afficher ${partner.name}`}
                    aria-current={isActive ? "true" : undefined}
                    className={cx(
                      "h-2 rounded-full transition-all duration-500",
                      isActive
                        ? "w-7 bg-blue-600 dark:bg-blue-400"
                        : [
                            "w-2 bg-slate-300",
                            "hover:bg-slate-400",
                            "dark:bg-slate-700",
                            "dark:hover:bg-slate-600",
                          ].join(" "),
                    )}
                  />
                );
              })}
            </div>

            <button
              type="button"
              onClick={handleNext}
              aria-label="Afficher le partenaire suivant"
              className="
                flex h-11 w-11 items-center justify-center
                rounded-full
                border border-slate-200
                bg-white
                text-slate-700
                shadow-lg shadow-slate-900/10
                transition
                duration-300
                active:scale-90
                dark:border-slate-700
                dark:bg-slate-900
                dark:text-white
              "
            >
              <FaChevronRight aria-hidden="true" />
            </button>
          </div>

          <p
            className="
              mt-4 text-center
              text-xs font-bold
              text-slate-400
              dark:text-slate-500
            "
          >
            Faites glisser pour découvrir nos partenaires
          </p>
        </div>

        {/* ===================================================== */}
        {/* DESKTOP : bandeau animé infini                         */}
        {/* ===================================================== */}

        <div className="mt-12 hidden md:block">
          <div
            className="
              partners-marquee-wrapper
              relative overflow-hidden
              py-8
            "
            style={{
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
              maskImage:
                "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
            }}
          >
            <div className="partners-marquee-track flex w-max">
              {/* Première série */}
              <div className="flex shrink-0 gap-6 pr-6">
                {safePartners.map((partner, index) => (
                  <PartnerCard
                    key={`original-${partner.name}`}
                    partner={partner}
                    eager={index < 4}
                  />
                ))}
              </div>

              {/* Duplication permettant l'animation infinie */}
              <div className="flex shrink-0 gap-6 pr-6" aria-hidden="true">
                {safePartners.map((partner) => (
                  <PartnerCard
                    key={`duplicate-${partner.name}`}
                    partner={partner}
                    duplicate
                  />
                ))}
              </div>
            </div>
          </div>

          <p
            className="
              mt-1 text-center
              text-xs font-bold uppercase
              tracking-[0.15em]
              text-slate-400
              dark:text-slate-500
            "
          >
            Survolez un partenaire pour interrompre le défilement
          </p>
        </div>
      </div>
    </section>
  );
}
